import { MutableRefObject, useState, useEffect, useCallback, useMemo } from 'react';

import _debounce from 'lodash/debounce';

export type UseContainerDimensionResult = {
  /** The offsetWidth: the size of the visual box including all the borders.  Calculated by adding width, padding and borders */
  width: number;

  /** The visual portion of the box content, not including borders or scroll bars , but includes padding  */
  clientWidth: number;

  height: number;

  /** The size of all of the box's content, including the parts that are currently hidden outside the scrolling area. */
  scrollWidth: number;
  scrollHeight: number;
};

/**
 * Given a ref in the dom, return its dimension (height and width) and listen
 * for window resize events for updates. Scroll height and width are returned
 * as well.  To determine if the element has scrollbars, you can check to see
 * if the scroll height/width is greater than the height/width.
 *
 * Cribbed and modified from: https://stackoverflow.com/a/60978633/29680
 *
 * @param ref A ref to a DOM element
 * @param forceUpdate An optional string, perhaps a guid, that will force a re-evaluation of the container dimensions when changed.
 *
 * @returns The height and width of the DOM element, or zeros if the ref is not initialized.
 */
export const useContainerDimensions = <T extends HTMLElement = HTMLDivElement>(
  ref: MutableRefObject<T | null>,

  // An optional parameter to force re-evaluation
  // Change this value and the dimensions will be re-computed
  forceUpdate?: string
): UseContainerDimensionResult => {
  const [dimensions, setDimensions] = useState({
    width: 0,
    clientWidth: 0,
    height: 0,
    scrollWidth: 0,
    scrollHeight: 0,
  });

  const getDimensions = useCallback(
    () => ({
      width: ref?.current?.offsetWidth || 0,
      clientWidth: ref?.current?.clientWidth || 0,
      height: ref?.current?.offsetHeight || 0,
      scrollWidth: ref?.current?.scrollWidth || 0,
      scrollHeight: ref?.current?.scrollHeight || 0,
    }),
    [ref]
  );

  const handleWindowResize = useMemo(() => {
    return _debounce(() => {
      setDimensions(getDimensions());
    }, 500);
  }, [getDimensions]);

  useEffect(() => {
    setDimensions(getDimensions());

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getDimensions, handleWindowResize, ref.current, forceUpdate]);

  return dimensions;
};
